import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { Player } from '../types/player';
import { Queue } from '../types/queue';
import { QueueService } from '../services/queueService';
import { QueueRotation } from '../utils/queueRotation';

interface QueueState {
  // 状态
  queue: Queue | null;
  currentPlayers: Player[];
  restingPlayers: Player[];
  isLoading: boolean;
  error: string | null;

  // 操作
  loadQueue: () => Promise<void>;
  rotateQueue: () => Promise<void>;
  updateQueue: (players: Player[]) => Promise<void>;
  initializeQueue: (players: Player[]) => Promise<void>;
  clearError: () => void;
  
  // 计算属性
  canStartMatch: () => boolean;
  getRotationPreview: (rounds?: number) => Array<{
    round: number;
    players: Player[];
    resting: Player[];
  }>;
}

export const useQueueStore = create<QueueState>()(
  subscribeWithSelector((set, get) => ({
    // 初始状态
    queue: null,
    currentPlayers: [],
    restingPlayers: [],
    isLoading: false,
    error: null,

    // 加载队列状态
    loadQueue: async () => {
      set({ isLoading: true, error: null });
      
      try {
        const queue = await QueueService.getCurrentQueue();
        
        if (queue) {
          const currentPlayers = QueueRotation.getCurrentPlayers(queue);
          const restingPlayers = QueueRotation.getRestingPlayers(queue);
          
          set({
            queue,
            currentPlayers,
            restingPlayers,
            isLoading: false,
          });
        } else {
          set({
            queue: null,
            currentPlayers: [],
            restingPlayers: [],
            isLoading: false,
          });
        }
      } catch (error) {
        set({
          error: error instanceof Error ? error.message : '加载队列失败',
          isLoading: false,
        });
      }
    },

    // 轮换队列
    rotateQueue: async () => {
      const { queue } = get();
      if (!queue) {
        set({ error: '队列未初始化' });
        return;
      }

      set({ isLoading: true, error: null });

      try {
        const rotatedQueue = await QueueService.rotateQueue(queue);
        const currentPlayers = QueueRotation.getCurrentPlayers(rotatedQueue);
        const restingPlayers = QueueRotation.getRestingPlayers(rotatedQueue);

        set({
          queue: rotatedQueue,
          currentPlayers,
          restingPlayers,
          isLoading: false,
        });
      } catch (error) {
        set({
          error: error instanceof Error ? error.message : '轮换队列失败',
          isLoading: false,
        });
      }
    },

    // 更新队列
    updateQueue: async (players: Player[]) => {
      const { queue } = get();
      if (!queue) {
        set({ error: '队列未初始化' });
        return;
      }

      set({ isLoading: true, error: null });

      try {
        const updatedQueue = await QueueService.updateQueue(
          queue.id,
          players,
          queue.currentPosition
        );
        
        const currentPlayers = QueueRotation.getCurrentPlayers(updatedQueue);
        const restingPlayers = QueueRotation.getRestingPlayers(updatedQueue);

        set({
          queue: updatedQueue,
          currentPlayers,
          restingPlayers,
          isLoading: false,
        });
      } catch (error) {
        set({
          error: error instanceof Error ? error.message : '更新队列失败',
          isLoading: false,
        });
      }
    },

    // 初始化队列
    initializeQueue: async (players: Player[]) => {
      set({ isLoading: true, error: null });

      try {
        const newQueue = await QueueService.initializeQueue(players);
        const currentPlayers = QueueRotation.getCurrentPlayers(newQueue);
        const restingPlayers = QueueRotation.getRestingPlayers(newQueue);

        set({
          queue: newQueue,
          currentPlayers,
          restingPlayers,
          isLoading: false,
        });
      } catch (error) {
        set({
          error: error instanceof Error ? error.message : '初始化队列失败',
          isLoading: false,
        });
      }
    },

    // 清除错误
    clearError: () => {
      set({ error: null });
    },

    // 检查是否可以开始比赛
    canStartMatch: () => {
      const { currentPlayers } = get();
      return QueueRotation.canStartMatch(currentPlayers);
    },

    // 获取轮换预览
    getRotationPreview: (rounds = 5) => {
      const { queue } = get();
      if (!queue) return [];
      
      return QueueRotation.getRotationPreview(queue.players, rounds);
    },
  }))
);

// 订阅队列变化，自动更新相关状态
useQueueStore.subscribe(
  (state) => state.queue,
  (queue) => {
    if (queue) {
      const currentPlayers = QueueRotation.getCurrentPlayers(queue);
      const restingPlayers = QueueRotation.getRestingPlayers(queue);
      
      useQueueStore.setState({
        currentPlayers,
        restingPlayers,
      });
    }
  }
);